// controllers/quotationController.js  (frontend)
import apiService from '../services/apiService';
import { authController } from './authController';

// ─── Internal helpers (invoiceController jaisa hi pattern) ────────────────────
const _getUser = () => authController.getCurrentUser();
const _getRole = () => _getUser()?.role || null;

const _guard = (allowedRoles) => {
  const role = _getRole();
  if (!role)                        return { blocked: true, reason: 'Not authenticated. Please log in.' };
  if (!allowedRoles.includes(role)) return { blocked: true, reason: `Access denied. Requires: ${allowedRoles.join(' or ')}` };
  return { blocked: false };
};

const ALLOWED = ['admin', 'super_admin', 'sales_manager', 'sales_rep'];

const quotationController = {
  getCurrentUser: () => _getUser(),
  getCurrentRole: () => _getRole(),
  isSuperAdmin:   () => _getRole() === 'super_admin',
  isAdmin:        () => ['admin', 'super_admin'].includes(_getRole()),

  // GET /api/quotations
  getAllQuotations: async (filters = {}) => {
    const g = _guard(ALLOWED);
    if (g.blocked) return { success: false, data: [], message: g.reason };
    try {
      const qs  = new URLSearchParams(filters).toString();
      const res = await apiService.get(qs ? `quotations?${qs}` : 'quotations');
      return { success: true, data: res.data || [], pagination: res.pagination || {} };
    } catch (e) { return { success: false, data: [], message: e.message || 'Failed to fetch quotations' }; }
  },

  getQuotationById: async (id) => {
    const g = _guard(ALLOWED);
    if (g.blocked) return { success: false, data: null, message: g.reason };
    try {
      const res = await apiService.get(`quotations/${id}`);
      return { success: true, data: res.data };
    } catch (e) { return { success: false, data: null, message: e.message || 'Quotation not found' }; }
  },

  createQuotation: async (data) => {
    const g = _guard(ALLOWED);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.post('quotations', data);
      return { success: true, data: res.data, message: 'Quotation created successfully' };
    } catch (e) { return { success: false, message: e.message || 'Failed to create quotation' }; }
  },

  updateQuotation: async (id, updates) => {
    const g = _guard(ALLOWED);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.put(`quotations/${id}`, updates);
      return { success: true, data: res.data, message: 'Quotation updated successfully' };
    } catch (e) { return { success: false, message: e.message || 'Failed to update quotation' }; }
  },

  updateStatus: async (id, status) => {
    const g = _guard(ALLOWED);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.patch(`quotations/${id}/status`, { status });
      return { success: true, data: res.data, message: 'Status updated successfully' };
    } catch (e) { return { success: false, message: e.message || 'Failed to update status' }; }
  },

  sendQuotation: async (id) => {
    const g = _guard(ALLOWED);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.patch(`quotations/${id}/send`, {});
      return { success: true, data: res.data, message: 'Quotation sent successfully' };
    } catch (e) { return { success: false, message: e.message || 'Failed to send quotation' }; }
  },

  // Quotation ko Sales Order mein convert karta hai — "Accepted" hone par
  convertToOrder: async (id, orderData = {}) => {
    const g = _guard(ALLOWED);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.post(`quotations/${id}/convert-to-order`, orderData);
      return { success: true, data: res.data, message: 'Converted to Sales Order successfully' };
    } catch (e) { return { success: false, message: e.message || 'Conversion failed' }; }
  },

  duplicateQuotation: async (id) => {
    const g = _guard(ALLOWED);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.post(`quotations/${id}/duplicate`, {});
      return { success: true, data: res.data, message: 'Quotation duplicated successfully' };
    } catch (e) { return { success: false, message: e.message || 'Failed to duplicate quotation' }; }
  },

  cancelQuotation: async (id, cancelReason = '') => {
    const g = _guard(ALLOWED);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.patch(`quotations/${id}/cancel`, { cancelReason });
      return { success: true, data: res.data, message: 'Quotation cancelled successfully' };
    } catch (e) { return { success: false, message: e.message || 'Failed to cancel quotation' }; }
  },

  deleteQuotation: async (id) => {
    const g = _guard(['super_admin', 'admin']);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      await apiService.delete(`quotations/${id}`);
      return { success: true, message: 'Quotation deleted successfully' };
    } catch (e) { return { success: false, message: e.message || 'Failed to delete quotation' }; }
  },

  getStatistics: async (filters = {}) => {
    const g = _guard(ALLOWED);
    if (g.blocked) return { success: false, data: null, message: g.reason };
    try {
      const qs  = new URLSearchParams(filters).toString();
      const res = await apiService.get(qs ? `quotations/statistics?${qs}` : 'quotations/statistics');
      return { success: true, data: res.data };
    } catch (e) { return { success: false, data: null, message: e.message }; }
  },
};

export default quotationController;